import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { getLocalHubConfig, saveLocalHubConfig, clearLocalHubConfig, printTestReceipt } from "../services/localPrint";
import { ICONS } from "../icons";
import Button from "../components/UI/Button";
import FloatingInput from "../components/UI/FloatingInput";
import PageHeader from "../components/UI/PageHeader";
import { notify } from "../utils/notify";
import { useTranslation } from "../context/I18nContext";

// LocalPrintSetup is a ONE-TIME, PER-TILL screen — "Offline Receipt
// Printing via a Local Print Hub + Per-Branch Auto-Print Toggle" (this
// feature's own plan doc). It has nothing to do with the admin-facing
// PrintAgents.js screen beyond where its two inputs come from: an admin
// generates a Hub URL + token there (see that page's own "Local Print
// Setup" modal) and hands them, once, to whoever sets up THIS specific
// browser/till — after that, this screen's only job is to remember them
// (in this browser's own localStorage, see localPrint.js) so Checkout.js
// can reach the hub directly while offline, with zero involvement from
// this app's own cloud server.
export default function LocalPrintSetup() {
  const { t } = useTranslation("local_print_setup");
  const navigate = useNavigate();

  const existing = getLocalHubConfig();
  const [hubUrl, setHubUrl] = useState(existing?.url || "");
  const [token, setToken] = useState(existing?.token || "");
  const [isConfigured, setIsConfigured] = useState(!!existing);
  const [testing, setTesting] = useState(false);

  const handleSave = () => {
    if (!hubUrl.trim() || !token.trim()) {
      notify.error(t("validation_both_required", "Both the hub URL and the token are required"));
      return;
    }
    if (saveLocalHubConfig(hubUrl, token)) {
      setIsConfigured(true);
      notify.success(t("notify_saved", "Local print hub saved on this till"));
    } else {
      notify.error(t("notify_save_failed", "This browser would not let us save that — private/incognito mode blocks it on some browsers"));
    }
  };

  const handleClear = () => {
    clearLocalHubConfig();
    setHubUrl("");
    setToken("");
    setIsConfigured(false);
    notify.success(t("notify_cleared", "Local print hub removed from this till"));
  };

  const handleTestPrint = async () => {
    if (!hubUrl.trim() || !token.trim()) {
      notify.error(t("validation_both_required", "Both the hub URL and the token are required"));
      return;
    }
    setTesting(true);
    try {
      const result = await printTestReceipt(hubUrl, token);
      if (result.success) {
        notify.success(t("notify_test_sent", "Test receipt sent — check the branch printer"));
      } else {
        notify.error(result.error || t("notify_test_failed", "The local hub rejected or could not be reached for this test print"));
      }
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="p-4 md:p-6 max-w-2xl mx-auto transition-all duration-300">
      <PageHeader className="flex items-center gap-4 mb-8 border-b border-gray-100 pb-6">
        <button onClick={() => navigate("/dashboard")} className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400">
          <ICONS.ChevronLeft className="w-6 h-6 rtl:rotate-180" />
        </button>
        <div className="flex items-center justify-center min-w-[48px] h-[48px] text-[var(--primary-color)]">
          <ICONS.Config size={40} className="shrink-0" />
        </div>
        <div>
          <h2 className="text-xl md:text-2xl font-bold" style={{ color: "var(--primary-color)" }}>{t("page_title", "Local Print Setup")}</h2>
          <p className="text-gray-400 text-xs md:text-sm font-medium">{t("page_subtitle", "Connect THIS till to its branch's local print hub, for receipts while offline")}</p>
        </div>
      </PageHeader>

      <div className="bg-white rounded-[2rem] border border-gray-100 p-6 md:p-8 space-y-6">
        <div className={`flex items-center gap-3 px-4 py-3 rounded-2xl ${isConfigured ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
          <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${isConfigured ? "bg-emerald-500" : "bg-amber-500"}`} />
          <p className="text-sm font-bold">
            {isConfigured ? t("status_configured", "This till is connected to a local print hub") : t("status_not_configured", "This till has no local print hub configured yet")}
          </p>
        </div>

        <div className="text-sm text-gray-500 leading-relaxed space-y-2">
          <p>{t("intro_line_1", "Ask an admin to open Print Agents, generate a token for the agent running at this branch, and give you both its Hub URL and the token.")}</p>
          <p className="font-bold text-gray-600">{t("intro_line_2", "Before saving here, visit the Hub URL once in this same browser and accept the certificate warning — the hub's certificate is self-signed, so the browser needs to trust it once, manually, per till.")}</p>
        </div>

        <div className="space-y-5">
          <FloatingInput
            label={t("field_hub_url_label", "Hub URL (e.g. https://192.168.1.50:8443)")}
            value={hubUrl}
            onChange={(e) => setHubUrl(e.target.value)}
            icon={ICONS.Config}
          />
          <FloatingInput
            label={t("field_token_label", "Local Hub Token")}
            value={token}
            onChange={(e) => setToken(e.target.value)}
            icon={ICONS.Lock}
          />
        </div>

        <div className="flex flex-col sm:flex-row gap-3 pt-2">
          <Button variant="primary" onClick={handleSave} className="flex-1">{t("btn_save", "Save to This Till")}</Button>
          <Button variant="secondary" onClick={handleTestPrint} loading={testing} className="flex-1">{t("btn_test_print", "Print Test Receipt")}</Button>
          {isConfigured && (
            <Button variant="danger" onClick={handleClear} className="sm:flex-none">{t("btn_clear", "Remove")}</Button>
          )}
        </div>
      </div>
    </div>
  );
}
